import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface HeroSlide {
  videoId: string;
  badge: string;
  title: string;
  description: string;
  image: string;
}

const slides: HeroSlide[] = [
  {
    videoId: "music-festival-live",
    badge: "娱乐精选",
    title: "夏日音乐节现场直击",
    description: "灯光、人浪与全场大合唱，把最炸裂的现场氛围一次带给你。",
    image:
      "https://readdy.ai/api/search-image?query=vibrant%20summer%20music%20festival%20crowd%20with%20warm%20stage%20lights%20and%20confetti%20at%20dusk%2C%20cinematic%20wide%20banner%2C%20energetic%20warm%20tones%2C%20ultra%20detailed%2C%20professional%20editorial%20photography&width=1600&height=900&seq=pp-hero-01&orientation=landscape",
  },
  {
    videoId: "mountain-sunrise",
    badge: "旅行推荐",
    title: "徒步看山顶日出",
    description: "翻过最后一道山脊，只为等一场在云海之上缓缓升起的日出。",
    image:
      "https://readdy.ai/api/search-image?query=breathtaking%20sunrise%20over%20mountain%20ranges%20with%20golden%20light%20and%20misty%20valley%2C%20cinematic%20wide%20banner%2C%20serene%20atmospheric%20tones%2C%20ultra%20detailed%2C%20professional%20editorial%20photography&width=1600&height=900&seq=pp-hero-02&orientation=landscape",
  },
  {
    videoId: "ai-future",
    badge: "科技前沿",
    title: "人工智能如何改变生活",
    description: "从出行到医疗，用通俗的方式看懂 AI 正在如何重塑我们的日常。",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20futuristic%20technology%20background%20with%20glowing%20network%20lines%20and%20floating%20data%20particles%2C%20cinematic%20wide%20banner%2C%20elegant%20deep%20tones%2C%20ultra%20detailed%2C%20professional%20editorial%20photography&width=1600&height=900&seq=pp-hero-03&orientation=landscape",
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (next: number) => setIndex((next + slides.length) % slides.length);

  return (
    <section className="relative w-full overflow-hidden rounded-lg bg-foreground-950" aria-label="精选推荐视频">
      <div className="relative h-[420px] w-full md:h-[560px]">
        {slides.map((slide, i) => (
          <div
            key={slide.videoId}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={`${slide.title} 精选视频封面`}
              title={`${slide.title} - 啪啪视频`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/25"></div>
          </div>
        ))}

        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <span className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-primary-500/90 px-3 py-1 text-xs font-medium text-background-50">
            <i className="ri-fire-line"></i>
            {slides[index].badge}
          </span>
          <h2 className="max-w-3xl font-heading text-2xl font-bold leading-tight text-white md:text-5xl">
            {slides[index].title}
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
            {slides[index].description}
          </p>
          <Link
            to={`/video/${slides[index].videoId}`}
            className="mt-7 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            <i className="ri-play-fill text-lg"></i>
            立即观看
          </Link>
        </div>

        <button
          type="button"
          onClick={() => goTo(index - 1)}
          aria-label="上一个推荐"
          className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors duration-200 hover:bg-black/60 md:left-6"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => goTo(index + 1)}
          aria-label="下一个推荐"
          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors duration-200 hover:bg-black/60 md:right-6"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.videoId}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到第 ${i + 1} 个推荐`}
              className={`h-2 cursor-pointer rounded-full transition-all duration-300 ${
                i === index ? "w-6 bg-primary-500" : "w-2 bg-white/60 hover:bg-white"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}